import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Lock, AlarmClock, CheckCircle2, Circle, ShoppingCart, Plus, Check, TriangleAlert } from 'lucide-react';
import { format, parseISO } from 'date-fns';

import { useTaskStore } from '@/stores/task-store';
import { useSeasonStore } from '@/stores/season-store';
import { useShoppingListStore } from '@/stores/shopping-list-store';
import { useWeatherTaskAlerts } from '@/stores/weather-task-store';
import { ShoppingListService } from '@/services/shopping-list-service';
import { db } from '@/services/storage/db';
import { Button } from '@/components/common/Button';
import { SnoozeSheet } from '@/components/tasks/SnoozeSheet';
import { ShoppingItemForm } from '@/components/shopping/ShoppingItemForm';
import type { Task } from '@/types/task';
import type { CreateShoppingListItemInput } from '@/types/shopping-list';
import { getTaskStatus, formatRelativeDueDate, formatAbsoluteDueDate, formatSnoozeLabel, isSnoozeActive } from '@/utils/task-display-utils';
import { getCategoryDisplayName } from '@/utils/category-utils';
import { localTodayISO } from '@/utils/date-utils';

const shoppingService = new ShoppingListService(db);

function StatusBanner({ status }: { status: ReturnType<typeof getTaskStatus> }) {
  const config = {
    done: {
      text: 'text-[var(--color-text-primary)]',
      label: 'Completed',
    },
    overdue: {
      text: 'text-[var(--color-text-primary)]',
      label: 'Overdue',
    },
    blocked: {
      text: 'text-[var(--color-text-primary)]',
      label: 'Blocked',
    },
    snoozed: {
      text: 'text-[var(--color-text-primary)]',
      label: 'Snoozed',
    },
    pending: {
      text: 'text-[var(--color-text-primary)]',
      label: 'Pending',
    },
  }[status];

  return (
    <div data-testid="status-banner">
      <p className="field-label mb-0.5">STATUS</p>
      <span
        data-testid="task-status-value"
        className={`text-body-sm font-medium ${config.text}`}
      >
        {config.label}
      </span>
    </div>
  );
}

function DetailField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="field-label mb-0.5">
        {label}
      </p>
      <div className="text-body-sm text-[var(--color-text-primary)]">{children}</div>
    </div>
  );
}

export function TaskDetailPage() {
  const { id: paramSeasonId, taskId } = useParams<{ id: string; taskId: string }>();
  const [searchParams] = useSearchParams();
  // Support both /seasons/:id/tasks/:taskId (old) and /prep/tasks/:taskId?season=<id> (new)
  const seasonId = paramSeasonId ?? searchParams.get('season') ?? undefined;
  const navigate = useNavigate();
  const { tasks, loadTasks, completeTask, uncompleteTask, removeTask, unsnoozeTask, snoozeTaskUntil } = useTaskStore();
  const { seasons, loadSeasons } = useSeasonStore();

  const { items: allShoppingItems, loadItems: loadShoppingItems, addItem: addShoppingItem } = useShoppingListStore();
  const { conflicts, dismissConflict, acceptSuggestion } = useWeatherTaskAlerts();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showSnooze, setShowSnooze] = useState(false);
  const [showShoppingForm, setShowShoppingForm] = useState(false);

  const today = localTodayISO();

  useEffect(() => {
    if (seasons.length === 0) void loadSeasons();
  }, [seasons.length, loadSeasons]);

  useEffect(() => {
    if (seasonId && tasks.length === 0) void loadTasks(seasonId);
  }, [seasonId, tasks.length, loadTasks]);

  useEffect(() => {
    void loadShoppingItems();
  }, [loadShoppingItems]);

  const task = tasks.find((t) => t.id === taskId);
  const weatherConflict = task ? (conflicts.find((c) => c.taskId === task.id) ?? null) : null;

  if (!task) {
    return (
      <div className="flex flex-col gap-4 py-8" data-testid="task-not-found">
        <p className="text-[var(--color-text-muted)] text-body-sm">Task not found.</p>
        <Link to={seasonId ? `/prep/tasks?season=${seasonId}` : '/prep/tasks'} className="text-body-sm text-[var(--color-accent)] hover:underline">
          Back to tasks
        </Link>
      </div>
    );
  }

  const status = getTaskStatus(task, today);
  const isCompleted = task.completed;
  const hasSnoozed = task.snoozedUntil && isSnoozeActive(task.snoozedUntil, today);

  // Find predecessor tasks
  const predecessorTasks = (task.dependsOn ?? [])
    .map((depId) => tasks.find((t) => t.id === depId))
    .filter((t): t is Task => !!t);

  // Find tasks that depend on this task
  const dependentTasks = tasks.filter((t) => (t.dependsOn ?? []).includes(task.id));

  // Shopping list integration
  const suggestions = shoppingService.suggestItemsForTask(task);
  const linkedShoppingItems = allShoppingItems.filter((i) => i.taskId === task.id);

  async function handleAddSuggestion(name: string, category: string) {
    await addShoppingItem({
      name,
      category,
      taskId: task!.id,
      propertyId: task!.propertyId,
      purchased: false,
    });
  }

  async function handleAddCustomShoppingItem(data: CreateShoppingListItemInput) {
    await addShoppingItem({ ...data, taskId: task!.id, propertyId: task!.propertyId ?? data.propertyId });
    setShowShoppingForm(false);
  }

  async function handleToggleComplete() {
    if (isCompleted) {
      await uncompleteTask(task!.id);
    } else {
      await completeTask(task!.id);
    }
    // Return to origin; fall back to the season checklist if no history entry exists
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(seasonId ? `/prep/tasks?season=${seasonId}` : '/prep/tasks');
    }
  }

  async function handleDelete() {
    if (!task || !seasonId) return;
    setIsDeleting(true);
    try {
      await removeTask(task.id);
      navigate(seasonId ? `/prep/tasks?season=${seasonId}` : '/prep/tasks');
    } catch {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  }

  async function handleSnooze(until: Date) {
    await snoozeTaskUntil(task!.id, until);
    setShowSnooze(false);
  }

  async function handleUnsnooze() {
    await unsnoozeTask(task!.id);
  }

  return (
    <div data-testid="task-detail-layout" data-margin-top="zero" className="flex flex-col gap-0 -mx-4 mt-0">
      <div className="px-4 pt-4 flex flex-col gap-5 pb-8">
        {/* Header nav */}
        <div className="flex items-center gap-3">
          <Link
            to={seasonId ? `/prep/tasks?season=${seasonId}` : '/prep/tasks'}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
            aria-label="Back to task list"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </Link>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-display-sm text-[var(--color-text-display)] leading-snug">
              {task.title}
            </h1>
            {task.category && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-body-xs mt-1 bg-[var(--color-elevated)] text-[var(--color-text-muted)] border border-[var(--color-border)]">
                {getCategoryDisplayName(task.category)}
              </span>
            )}
          </div>
        </div>

        {/* Weather conflict inline banner */}
        {weatherConflict && (
          <div
            className="flex flex-col gap-2 px-4 py-3 rounded-lg bg-amber-900/20 border border-amber-700/40"
            data-testid="weather-conflict-banner"
          >
            <div className="flex items-center gap-2">
              <TriangleAlert size={13} className="text-amber-400 flex-shrink-0" />
              <span className="field-label text-amber-400">
                Weather Alert
              </span>
            </div>
            <p className="text-body-xs text-[var(--color-text-secondary)]">
              <span className="text-amber-300">{weatherConflict.weatherConcern}</span>
              {weatherConflict.suggestedDate && (
                <> — suggested date: <span className="font-medium text-[var(--color-text-primary)]">{format(parseISO(weatherConflict.suggestedDate), 'MMM d')}</span>
                  {weatherConflict.suggestedReason && (
                    <span className="text-[var(--color-text-placeholder)]"> ({weatherConflict.suggestedReason})</span>
                  )}
                </>
              )}
            </p>
            <div className="flex gap-2 pt-0.5">
              {weatherConflict.suggestedDate && (
                <button
                  type="button"
                  onClick={() => void acceptSuggestion(weatherConflict.taskId, weatherConflict.suggestedDate!)}
                  className="px-3 py-1 rounded-md text-body-xs font-medium bg-[var(--color-accent-hover)] text-[var(--color-text-primary)] hover:bg-[var(--color-accent)] transition-colors"
                  data-testid="banner-reschedule-button"
                >
                  Reschedule to {format(parseISO(weatherConflict.suggestedDate), 'MMM d')}
                </button>
              )}
              <button
                type="button"
                onClick={() => dismissConflict(weatherConflict.taskId)}
                className="px-3 py-1 rounded-md text-body-xs text-[var(--color-text-placeholder)] hover:text-[var(--color-text-muted)] transition-colors"
                data-testid="banner-dismiss-button"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Detail fields card */}
        <div data-testid="task-detail-card" className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card p-4 flex flex-col gap-4">
          <DetailField label="Due Date">
            {formatAbsoluteDueDate(task.dueDate)}{' '}
            <span className="text-[var(--color-text-muted)]">
              ({formatRelativeDueDate(task.dueDate, today)})
            </span>
          </DetailField>

          {task.timingAnchor && task.offsetDays !== undefined && (
            <DetailField label="Timing">
              {task.timingAnchor === 'opener_relative'
                ? `${Math.abs(task.offsetDays)} days ${task.offsetDays < 0 ? 'before' : 'after'} opener`
                : `Calendar — ${format(parseISO(task.dueDate), 'MMM d')}`}
            </DetailField>
          )}

          {/* Status field — between TIMING and ACTION/NOTES */}
          <StatusBanner status={status} />

          {task.selectedAction && (
            <DetailField label="Action">{task.selectedAction}</DetailField>
          )}

          {task.notes && (
            <DetailField label="Notes">{task.notes}</DetailField>
          )}

          {hasSnoozed && task.snoozedUntil && (
            <DetailField label="Snoozed">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1 text-[#5a7a8a]">
                  <AlarmClock size={14} />
                  {formatSnoozeLabel(task.snoozedUntil)}
                </span>
                <button
                  type="button"
                  onClick={handleUnsnooze}
                  className="text-body-xs text-[var(--color-accent)] hover:underline"
                  data-testid="unsnooze-button"
                >
                  Unsnooze
                </button>
              </div>
            </DetailField>
          )}
        </div>

        {/* Shopping List section */}
        {(suggestions.length > 0 || linkedShoppingItems.length > 0) && (
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card p-4 flex flex-col gap-3" data-testid="shopping-section">
            <div className="flex items-center justify-between gap-2">
              <p className="field-label flex items-center gap-1.5">
                <ShoppingCart size={13} />
                Shopping List
              </p>
              <button
                type="button"
                onClick={() => setShowShoppingForm(true)}
                className="text-body-xs text-[var(--color-accent)] hover:underline flex items-center gap-1"
                data-testid="add-custom-shopping-item"
              >
                <Plus size={12} />
                Add custom
              </button>
            </div>

            {/* Suggestions */}
            {suggestions.length > 0 && (
              <div className="flex flex-col gap-2">
                <p className="text-body-xs text-[var(--color-text-muted)]">Suggested for this task:</p>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((s) => {
                    const alreadyAdded = linkedShoppingItems.some(
                      (i) => i.name.toLowerCase() === s.name.toLowerCase(),
                    );
                    return (
                      <button
                        key={s.name}
                        type="button"
                        onClick={() => !alreadyAdded && void handleAddSuggestion(s.name, s.category)}
                        disabled={alreadyAdded}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-body-xs border transition-colors ${
                          alreadyAdded
                            ? 'bg-[var(--color-done-bg)] border-[var(--color-done)] text-[var(--color-done)] cursor-default'
                            : 'bg-[var(--color-elevated)] border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-accent)] hover:text-[var(--color-accent)]'
                        }`}
                        data-testid={`suggestion-${s.name.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        {alreadyAdded ? <Check size={11} strokeWidth={3} /> : <Plus size={11} />}
                        {s.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Already-added items */}
            {linkedShoppingItems.length > 0 && (
              <div className="flex flex-col gap-1.5 border-t border-[var(--color-border)] pt-3">
                {linkedShoppingItems.map((item) => (
                  <div key={item.id} className="flex items-center gap-2 text-body-xs">
                    <div className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center flex-shrink-0 ${item.purchased ? 'bg-[var(--color-done)] border-[var(--color-done)]' : 'border-[var(--color-border)]'}`}>
                      {item.purchased && <Check size={9} className="text-white" strokeWidth={3} />}
                    </div>
                    <span className={`text-[var(--color-text-secondary)] ${item.purchased ? 'line-through text-[var(--color-text-muted)]' : ''}`}>
                      {item.name}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Dependencies */}
        {predecessorTasks.length > 0 && (
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card p-4 flex flex-col gap-3">
            <p className="field-label">
              Depends On
            </p>
            {predecessorTasks.map((dep) => (
              <Link
                key={dep.id}
                to={`/prep/tasks/${dep.id}${seasonId ? `?season=${seasonId}` : ''}`}
                className="flex items-center gap-2 text-body-sm text-[var(--color-text-primary)] hover:text-[var(--color-accent)] transition-colors"
                data-testid={`dep-task-${dep.id}`}
              >
                {dep.completed ? (
                  <CheckCircle2 size={16} className="text-[var(--color-done)] flex-shrink-0" />
                ) : (
                  <Circle size={16} className="text-[var(--color-text-muted)] flex-shrink-0" />
                )}
                {dep.title}
              </Link>
            ))}
          </div>
        )}

        {/* Blocked indicator detail */}
        {status === 'blocked' && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--color-elevated)]/50 border border-[var(--color-border)]" data-testid="blocked-detail">
            <Lock size={14} className="text-[var(--color-text-muted)] flex-shrink-0" />
            <p className="text-body-xs text-[var(--color-text-secondary)]">
              This task is blocked by incomplete prerequisites. Complete the tasks above first.
            </p>
          </div>
        )}

        {/* Dependent tasks */}
        {dependentTasks.length > 0 && (
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card p-4 flex flex-col gap-3">
            <p className="field-label">
              Waiting on This Task
            </p>
            {dependentTasks.map((dep) => (
              <Link
                key={dep.id}
                to={`/prep/tasks/${dep.id}${seasonId ? `?season=${seasonId}` : ''}`}
                className="text-body-sm text-[var(--color-text-secondary)] hover:text-[var(--color-accent)] transition-colors"
                data-testid={`dependent-task-${dep.id}`}
              >
                {dep.title}
              </Link>
            ))}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex flex-col gap-3 mt-2">
          <Button
            variant="primary"
            onClick={handleToggleComplete}
            className="w-full"
            data-testid="toggle-complete-button"
          >
            {isCompleted ? 'Mark Incomplete' : 'Mark Complete'}
          </Button>

          <div className="flex gap-3">
            <Link to={`/prep/tasks/${task.id}/edit${seasonId ? `?season=${seasonId}` : ''}`} className="flex-1">
              <Button variant="ghost" className="w-full" data-testid="edit-button">
                Edit
              </Button>
            </Link>
            <Button
              variant="ghost"
              onClick={() => setShowSnooze(true)}
              data-testid="snooze-button"
            >
              Snooze
            </Button>
            <Button
              variant="danger"
              onClick={() => setShowDeleteConfirm(true)}
              data-testid="delete-button"
            >
              Delete
            </Button>
          </div>
        </div>
      </div>

      {/* Shopping item form */}
      {showShoppingForm && (
        <ShoppingItemForm
          defaultTaskId={task.id}
          defaultPropertyId={task.propertyId}
          onSubmit={handleAddCustomShoppingItem}
          onClose={() => setShowShoppingForm(false)}
        />
      )}

      {/* Snooze sheet */}
      {showSnooze && (
        <SnoozeSheet
          onSnooze={handleSnooze}
          onClose={() => setShowSnooze(false)}
        />
      )}

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60">
          <div className="bg-[var(--color-elevated)] border border-[var(--color-border)] rounded-xl shadow-card-lg w-full max-w-sm p-6 flex flex-col gap-4" data-testid="delete-dialog">
            <div className="flex flex-col gap-2">
              <h2 className="font-display text-display-sm text-[var(--color-text-display)]">
                Delete Task?
              </h2>
              <p className="text-body-sm text-[var(--color-text-secondary)]">
                Delete{' '}
                <strong className="text-[var(--color-text-primary)]">"{task.title}"</strong>?
                This cannot be undone.
              </p>
              {dependentTasks.length > 0 && (
                <p className="text-body-xs text-[var(--color-accent)] mt-1" data-testid="dependent-warning">
                  Warning: {dependentTasks.length} other task{dependentTasks.length > 1 ? 's' : ''} depend{dependentTasks.length === 1 ? 's' : ''} on this task. Deleting it will unblock them.
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <Button
                variant="danger"
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex-1"
                data-testid="confirm-delete-button"
              >
                {isDeleting ? 'Deleting…' : 'Delete Task'}
              </Button>
              <Button
                variant="ghost"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
